import React, { useMemo, useRef, useEffect } from "react";
import moment from "moment";
import {
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../Component";
import MedicalPartnerListOrganizationColumn from "./MedicalPartnerListOrganizationColumn";
import MedicalPartnerListPrimaryUserCell from "./MedicalPartnerListPrimaryUserCell";
import MedicalPartnerListTcSwitch from "./MedicalPartnerListTcSwitch";
import MedicalPartnerListStatusSelect from "./MedicalPartnerListStatusSelect";
import MedicalPartnerListMtSwitch from "./MedicalPartnerListMtSwitch";
import MedicalPartnerListRowTools from "./MedicalPartnerListRowTools";
import MedicalPartnerListEmptyState from "./MedicalPartnerListEmptyState";

const MedicalPartnerListTable = ({
  currentItems,
  highlightedPartnerId,
  onOpenDossier,
  selectPartnerRow,
  activeTab,
  hasListFilters,
  openAddPartnerModal,
  loading,
  filteredCount,
  itemPerPage,
  currentPage,
  paginate,
  onConfirmDelete,
  confirmApprovePartner,
  tcBusyId,
  onTcSwitchChange,
  statusBusyId,
  onStatusSelect,
  onMtSwitchChange,
  onAssignPrimaryUser,
  onPatchPartner,
  patchBusyPartnerId,
  selectionEnabled = false,
  selectedIds = [],
  onToggleRow,
  onToggleAllOnPage,
}) => {
  const headerCheckRef = useRef(null);
  const pageIds = useMemo(
    () =>
      (currentItems || [])
        .map((r) => (r && r._id ? String(r._id) : null))
        .filter(Boolean),
    [currentItems]
  );
  const selectedSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedSet.has(id));

  useEffect(() => {
    const el = headerCheckRef.current;
    if (!el) return;
    el.indeterminate = someOnPageSelected && !allOnPageSelected;
  }, [someOnPageSelected, allOnPageSelected]);

  return (
  <>
    <DataTableBody compact>
      <DataTableHead>
        {selectionEnabled ? (
          <DataTableRow size="sm" className="medical-partner-list__select-col text-center">
            <span className="sr-only">Select row</span>
            <input
              ref={headerCheckRef}
              type="checkbox"
              className="medical-partner-list__select"
              checked={allOnPageSelected}
              onChange={() => onToggleAllOnPage?.(pageIds)}
              aria-label="Select all partners on this page"
            />
          </DataTableRow>
        ) : null}
        <DataTableRow size="lg" className="medical-partner-list__org-col">
          <span className="sub-text">Organization</span>
        </DataTableRow>
        <DataTableRow size="md" className="medical-partner-list__status-col">
          <span className="sub-text">Workflow</span>
        </DataTableRow>
        <DataTableRow size="md">
          <span className="sub-text">Contact</span>
        </DataTableRow>
        <DataTableRow size="md" className="medical-partner-list__tc-col text-nowrap">
          <span className="sub-text">Teleconsultation</span>
        </DataTableRow>
        <DataTableRow size="md" className="medical-partner-list__mt-col text-wrap">
          <span className="sub-text medical-partner-list__mt-head">Medical tourism</span>
        </DataTableRow>
        <DataTableRow size="sm">
          <span className="sub-text">Created</span>
        </DataTableRow>
        <DataTableRow className="nk-tb-col-tools text-end medical-partner-list__actions-col">
          <span className="sub-text">Actions</span>
        </DataTableRow>
      </DataTableHead>
      {currentItems.length > 0 ? (
        currentItems.map((item, index) => {
          if (!item) return null;
          const rowId = item._id || item.id || `medical-partner-${index}`;
          const tcApproved = (item.tcApprovalStatus || "off") === "approved";
          const tcBusy = tcBusyId && String(tcBusyId) === String(item._id);
          const statusBusy = statusBusyId && String(statusBusyId) === String(item._id);
          const rowPatchBusy = patchBusyPartnerId && String(patchBusyPartnerId) === String(item._id);

          return (
            <DataTableItem
              key={rowId}
              className={highlightedPartnerId === item._id ? "bg-lighter" : ""}
              onClick={(e) => selectPartnerRow(item._id, e)}
            >
              {selectionEnabled ? (
                <DataTableRow size="sm" className="medical-partner-list__select-col text-center" dataLabel="Select">
                  <input
                    type="checkbox"
                    className="medical-partner-list__select"
                    checked={selectedSet.has(String(item._id))}
                    onChange={() => onToggleRow?.(String(item._id))}
                    aria-label={`Select ${item.organization_name || "partner"}`}
                  />
                </DataTableRow>
              ) : null}
              <DataTableRow size="lg" className="medical-partner-list__org-col" dataLabel="Organization">
                <MedicalPartnerListOrganizationColumn
                  item={item}
                  rowId={rowId}
                  onPatchPartner={onPatchPartner}
                  patchBusy={Boolean(rowPatchBusy)}
                />
              </DataTableRow>
              <DataTableRow size="md" className="medical-partner-list__status-col" dataLabel="Workflow">
                <MedicalPartnerListStatusSelect
                  rowId={rowId}
                  item={item}
                  disabled={statusBusy || Boolean(rowPatchBusy)}
                  onStatusChange={onStatusSelect}
                />
              </DataTableRow>
              <DataTableRow size="md" dataLabel="Contact">
                <MedicalPartnerListPrimaryUserCell item={item} onAssignPrimaryUser={onAssignPrimaryUser} />
              </DataTableRow>
              <DataTableRow size="md" className="medical-partner-list__tc-col text-nowrap" dataLabel="TC">
                <MedicalPartnerListTcSwitch
                  rowId={rowId}
                  item={item}
                  checked={tcApproved}
                  disabled={tcBusy}
                  onTcSwitchChange={onTcSwitchChange}
                />
              </DataTableRow>
              <DataTableRow size="md" className="medical-partner-list__mt-col" dataLabel="Medical tourism">
                <MedicalPartnerListMtSwitch
                  rowId={rowId}
                  item={item}
                  disabled={Boolean(rowPatchBusy)}
                  onMtSwitchChange={onMtSwitchChange}
                />
              </DataTableRow>
              <DataTableRow size="sm" dataLabel="Created">
                <span>{item.createdAt ? moment(item.createdAt).format("DD/MM/YY") : "—"}</span>
              </DataTableRow>
              <DataTableRow className="nk-tb-col-tools text-end medical-partner-list__actions-col" dataLabel="Actions">
                <MedicalPartnerListRowTools
                  rowId={rowId}
                  item={item}
                  onOpenDossier={onOpenDossier}
                  onConfirmDelete={onConfirmDelete}
                  onConfirmApprove={confirmApprovePartner}
                />
              </DataTableRow>
            </DataTableItem>
          );
        })
      ) : (
        <MedicalPartnerListEmptyState
          activeTab={activeTab}
          hasListFilters={hasListFilters}
          openAddPartnerModal={openAddPartnerModal}
          loading={loading}
        />
      )}
    </DataTableBody>
    <div className="card-inner">
      {currentItems.length > 0 ? (
        <PaginationComponent itemPerPage={itemPerPage} totalItems={filteredCount} paginate={paginate} currentPage={currentPage} />
      ) : (
        <div className="text-center">
          <span className="text-silent">
            {activeTab === "pending" ? "No pending requests" : activeTab === "active" ? "No live partner" : "No data found"}
          </span>
        </div>
      )}
    </div>
  </>
  );
};

export default MedicalPartnerListTable;
